import api from 'lib/api'
import classNames from 'clsx'
import { Icon } from 'components'
import { parseJSONCurriculum } from './utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type UploadStatus = 'idle' | 'loading' | 'success' | 'error'

interface FileInfo {
  name: string
  size: number
}

interface CurriculumData {
  [key: string]: unknown
}

interface FileUploadCurriculumProps {
  onUploadSuccess: (data: CurriculumData) => void
  onUploadError?: () => void
}

const STORAGE_KEY = 'curriculum-pdf-name'

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
  })
}

export const FileUploadCurriculum = ({
  onUploadSuccess,
  onUploadError
}: FileUploadCurriculumProps) => {
  const [status, setStatus] = useState<UploadStatus>('idle')
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const fileStorage = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
    if (fileStorage?.name) {
      setFileInfo({ name: fileStorage.name, size: fileStorage.size })
      setStatus('success')
    }
  }, [])

  const uploadFile = useCallback(
    async (file: File) => {
      if (file.type !== 'application/pdf') {
        setStatus('error')
        setErrorMessage('Formato inválido. Envie um arquivo PDF.')
        return
      }

      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        setStatus('error')
        setErrorMessage('Arquivo muito grande. O tamanho máximo é 5MB.')
        return
      }

      setFileInfo({ name: file.name, size: file.size })
      setStatus('loading')
      setErrorMessage('')

      try {
        const data = await fileToBase64(file)

        const response = await api('/user/applicant/curriculum-pdf', {
          method: 'POST',
          data: { data, format: 'generic' }
        })

        if (response.status !== 200) {
          throw new Error('Falha ao processar o currículo')
        }

        const result = response.data
        setStatus('success')
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ name: file.name, size: file.size })
        )

        onUploadSuccess(parseJSONCurriculum(result))
      } catch {
        setStatus('error')
        setErrorMessage(
          'Não foi possível processar o currículo. Tente novamente.'
        )
        onUploadError?.()
      }
    },
    [onUploadSuccess, onUploadError]
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) uploadFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) uploadFile(file)
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setStatus('idle')
    setFileInfo(null)
    setErrorMessage('')
    localStorage.removeItem(STORAGE_KEY)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleRetry = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setStatus('idle')
    setErrorMessage('')
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  const iconFace = useMemo(() => {
    if (status === 'success') {
      return 'check'
    } else if (status === 'error') {
      return 'close'
    }
    return 'upload'
  }, [status])

  return (
    <label
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={classNames(
        'border-2 border-dashed rounded-xl p-5 flex items-center cursor-pointer transition-all duration-200 relative group',
        {
          'border-gray-300 hover:border-purple-400 hover:bg-purple-50/50':
            status === 'idle' && !isDragging,
          'border-purple-500 bg-purple-50/70 scale-[1.01]': isDragging,
          'border-purple-300 bg-purple-50/30': status === 'loading',
          'border-green-400 bg-green-50/50 hover:bg-green-50/70':
            status === 'success',
          'border-red-400 bg-red-50/50 hover:bg-red-50/70': status === 'error'
        }
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={handleFileChange}
        disabled={status === 'loading'}
      />

      <div className="flex items-center gap-3 w-full">
        <div
          className={classNames(
            'w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-all duration-200',
            {
              'bg-purple-900 text-white group-hover:bg-purple-700 group-hover:shadow-md group-hover:shadow-purple-200':
                status === 'idle',
              'bg-purple-600 text-white': status === 'loading',
              'bg-green-500 text-white': status === 'success',
              'bg-red-500 text-white': status === 'error'
            }
          )}
        >
          {status === 'loading' ? (
            <svg
              className="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <Icon face={iconFace} size="lg" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {status === 'idle' && (
            <>
              <p className="font-bold text-gray-800 text-body">
                Anexar currículo
              </p>
              <p className="text-body2 text-gray-500">
                Arraste o PDF aqui ou{' '}
                <span className="text-purple-700 font-semibold group-hover:underline">
                  selecione
                </span>{' '}
                na sua máquina.
              </p>
            </>
          )}

          {status === 'loading' && (
            <>
              <p className="font-bold text-purple-800 text-body">
                Processando currículo...
              </p>
              <p className="text-body2 text-purple-600">{fileInfo?.name}</p>
            </>
          )}

          {status === 'success' && fileInfo && (
            <>
              <p className="font-bold text-green-800 text-body truncate">
                {fileInfo.name}
              </p>
              <p className="text-body2 text-green-600">
                {formatFileSize(fileInfo.size)} • Currículo importado com
                sucesso
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <p className="font-bold text-red-800 text-body">Falha no envio</p>
              <p className="text-body2 text-red-600">{errorMessage}</p>
            </>
          )}
        </div>

        {(status === 'success' || status === 'error') && (
          <button
            type="button"
            onClick={status === 'error' ? handleRemove : handleRetry}
            title={status === 'error' ? 'Remover arquivo' : 'Tentar novamente'}
            className={classNames(
              'shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 transition-all duration-200',
              status === 'error' && 'hover:text-red-500 hover:bg-red-50',
              status === 'success' && 'hover:text-purple-600 hover:bg-purple-50'
            )}
          >
            <Icon face={status === 'error' ? 'delete' : 'refresh'} size="md" />
          </button>
        )}
      </div>
    </label>
  )
}
